/**
 * Glue between lib/marching-cubes.js (model frame: x, y, depth) and the 3D scene
 * (x = east, y = -depth, z = north, metres around the scene origin). Pure, no three.js.
 */
import { lonLatToLocal } from './view-sync';
import { sampleGrid } from './marching-cubes';
import { colourTexturePixels } from './model-colour';

const STOPS = [[37, 99, 235], [6, 182, 212], [52, 211, 153], [251, 191, 36], [251, 146, 60], [248, 113, 113]];

/** Blue -> red ramp (same stops as the fence diagrams); t in 0..1 -> [r,g,b] 0..255. */
export function rampColor(t) {
  const p = Math.max(0, Math.min(1, t)) * (STOPS.length - 1);
  const lo = Math.floor(p), hi = Math.min(STOPS.length - 1, lo + 1), f = p - lo;
  return [0, 1, 2].map((k) => STOPS[lo][k] + (STOPS[hi][k] - STOPS[lo][k]) * f);
}

/**
 * Affine from model (x, y, depth) to the scene, taken from the cells' lon/lat (GeoRef-derived):
 *   scene.x = a*x + b*y + tx,  scene.z = c*x + d*y + tz,  scene.y = -depth
 * Returns null when the cells carry no lon/lat or the grid is degenerate.
 */
export function gridToScene(cells, grid, origin) {
  const { x: xs, y: ys } = grid.coords;
  if (xs.length < 2 || ys.length < 2 || !origin) return null;
  const want = new Map();
  [[xs[0], ys[0]], [xs[1], ys[0]], [xs[0], ys[1]]].forEach(([x, y]) => want.set(`${x}|${y}`, null));
  for (const c of cells) {
    const key = `${Math.round(c.x * 1e6) / 1e6}|${Math.round(c.y * 1e6) / 1e6}`;
    if (want.has(key) && !want.get(key) && typeof c.lon === 'number') want.set(key, lonLatToLocal([c.lon, c.lat], origin));
  }
  const p00 = want.get(`${xs[0]}|${ys[0]}`), p10 = want.get(`${xs[1]}|${ys[0]}`), p01 = want.get(`${xs[0]}|${ys[1]}`);
  if (!p00 || !p10 || !p01) return null;
  const dx = xs[1] - xs[0], dy = ys[1] - ys[0];
  const a = (p10[0] - p00[0]) / dx, c = (p10[1] - p00[1]) / dx;
  const b = (p01[0] - p00[0]) / dy, d = (p01[1] - p00[1]) / dy;
  return { a, b, c, d, tx: p00[0] - a * xs[0] - b * ys[0], tz: p00[1] - c * xs[0] - d * ys[0] };
}

/** Inverse of gridToScene: scene [x, y, z] -> model [x, y, depth]. */
export function sceneToModel(aff, [sx, sy, sz]) {
  const det = aff.a * aff.d - aff.b * aff.c;
  const px = sx - aff.tx, pz = sz - aff.tz;
  return [(aff.d * px - aff.b * pz) / det, (-aff.c * px + aff.a * pz) / det, -sy];
}

/** Map a model-frame mesh into the scene. Normals are rotated; winding flips if the map mirrors. */
export function meshToScene(mesh, aff) {
  const { positions, normals, indices } = mesh;
  const n = positions.length / 3;
  const P = new Float32Array(positions.length), N = new Float32Array(normals.length);
  for (let i = 0; i < n; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    P[i * 3] = aff.a * x + aff.b * y + aff.tx;
    P[i * 3 + 1] = -z;
    P[i * 3 + 2] = aff.c * x + aff.d * y + aff.tz;
    // gradient-style normals transform with the inverse transpose
    const gx = normals[i * 3], gy = normals[i * 3 + 1], gz = normals[i * 3 + 2];
    const det = aff.a * aff.d - aff.b * aff.c;
    let nx = (aff.d * gx - aff.c * gy) / det, nz = (-aff.b * gx + aff.a * gy) / det, ny = -gz;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    N[i * 3] = nx; N[i * 3 + 1] = ny; N[i * 3 + 2] = nz;
  }
  // linear part [[a,b,0],[0,0,-1],[c,d,0]] has determinant a*d - b*c; a mirror flips the winding
  const flip = aff.a * aff.d - aff.b * aff.c < 0;
  let I = indices;
  if (flip) {
    I = new Uint32Array(indices.length);
    for (let t = 0; t < indices.length; t += 3) { I[t] = indices[t]; I[t + 1] = indices[t + 2]; I[t + 2] = indices[t + 1]; }
  }
  return { positions: P, normals: N, indices: I, vertexCount: mesh.vertexCount, triangleCount: mesh.triangleCount };
}

/** Per-vertex RGB (0..1) for an iso-surface: constant ramp colour at the level, or by depth. */
export function isoColors(mesh, mode, levelT, flatRgb = null) {
  const n = mesh.positions.length / 3;
  const out = new Float32Array(n * 3);
  let lo = Infinity, hi = -Infinity;
  if (mode === 'depth') for (let i = 0; i < n; i++) { const y = mesh.positions[i * 3 + 1]; if (y < lo) lo = y; if (y > hi) hi = y; }
  const span = hi > lo ? hi - lo : 1;
  const flat = flatRgb || rampColor(levelT);
  for (let i = 0; i < n; i++) {
    const c = mode === 'depth' ? rampColor(1 - (mesh.positions[i * 3 + 1] - lo) / span) : flat;
    out[i * 3] = c[0] / 255; out[i * 3 + 1] = c[1] / 255; out[i * 3 + 2] = c[2] / 255;
  }
  return out;
}

/** Plane normal (scene frame, y up) from azimuth (deg clockwise from north) and dip (0 = horizontal, 90 = vertical). */
export function planeNormal(azimuthDeg, dipDeg) {
  const az = (azimuthDeg * Math.PI) / 180, dip = (dipDeg * Math.PI) / 180;
  return [Math.sin(az) * Math.sin(dip), Math.cos(dip), Math.cos(az) * Math.sin(dip)];
}

/** Orthonormal in-plane axes for a unit normal. */
export function planeBasis(n) {
  const ref = Math.abs(n[1]) > 0.9 ? [0, 0, 1] : [0, 1, 0];
  let u = [ref[1] * n[2] - ref[2] * n[1], ref[2] * n[0] - ref[0] * n[2], ref[0] * n[1] - ref[1] * n[0]];
  const ul = Math.hypot(...u) || 1;
  u = u.map((q) => q / ul);
  const v = [n[1] * u[2] - n[2] * u[1], n[2] * u[0] - n[0] * u[2], n[0] * u[1] - n[1] * u[0]];
  return { u, v };
}

/** Value of the sorted array `a` closest to v. */
function nearest(a, v) {
  let lo = 0, hi = a.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (a[mid] <= v) lo = mid; else hi = mid; }
  return Math.abs(a[hi] - v) < Math.abs(a[lo] - v) ? a[hi] : a[lo];
}

/**
 * Resample a grid on a plane (scene frame) into an RGBA texture.
 *  plane: { centre:[x,y,z], normal:[nx,ny,nz] (unit) }, size in metres, res texels per side.
 *  range: [min, max] of the grid values for the colour ramp. NaN / outside -> transparent.
 *  scale: optional makeColourScale result; colours the slice with the model colour table instead of the ramp.
 *  belowDoi: optional predicate on { x, y, depth } (lib/inversion-results.makeDoiMask); texels below the DOI stay
 *  transparent. The point is snapped to the nearest grid column so the per-column DOI lookup matches.
 */
export function planeSlice(grid, aff, plane, size, res = 96, range = grid.range, scale = null, belowDoi = null) {
  const { u, v } = planeBasis(plane.normal);
  const span = range[1] > range[0] ? range[1] - range[0] : 1;
  const ohm = new Float64Array(res * res); // resistivity per pixel, 0 = outside the model
  const data = new Uint8Array(res * res * 4);
  let hits = 0;
  for (let j = 0; j < res; j++) {
    for (let i = 0; i < res; i++) {
      const s = (i / (res - 1) - 0.5) * size, t = (j / (res - 1) - 0.5) * size;
      const p = [0, 1, 2].map((k) => plane.centre[k] + u[k] * s + v[k] * t);
      const [mx, my, md] = sceneToModel(aff, p);
      const val = sampleGrid(grid, mx, my, md);
      if (Number.isNaN(val)) continue;
      if (belowDoi && belowDoi({ x: nearest(grid.coords.x, mx), y: nearest(grid.coords.y, my), depth: md })) continue;
      const o = (j * res + i) * 4;
      if (scale) ohm[j * res + i] = 10 ** val; // the grid holds log10 resistivity
      else {
        const c = rampColor((val - range[0]) / span);
        data[o] = c[0]; data[o + 1] = c[1]; data[o + 2] = c[2]; data[o + 3] = 255;
      }
      hits++;
    }
  }
  // with a colour scale (lib/model-colour) the model's table, banding and per-level opacity colour the slice
  return { data: scale ? colourTexturePixels(ohm, res, res, scale) : data, res, hits };
}
